import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  EventSponsorSettings,
  type SponsorsSectionConfig,
} from '../components/EventSponsorSettings';
import api from '../config/api';

const { Title, Text } = Typography;

// A stored config may still hold an unknown mode, which the server now rejects;
// without this fallback such an event's pricing tab could never be saved.
const ARAZASI_MODOK = ['fixed', 'flags', 'ticketType'] as const;
type ArazasiMod = (typeof ARAZASI_MODOK)[number];

function biztonsagosMod(stored: unknown): ArazasiMod {
  return ARAZASI_MODOK.includes(stored as ArazasiMod) ? (stored as ArazasiMod) : 'ticketType';
}

type RegistrantType = 'company' | 'individual';
type CompanyField = 'taxNumber' | 'name' | 'address';

interface PriceFlagDef {
  key: string;
  label: string;
}
interface PriceMatrixRow {
  conditions: Record<string, boolean>;
  price: number;
}

interface EventConfig {
  registration: {
    allowedRegistrantTypes: RegistrantType[];
    companyFieldsRequired: CompanyField[];
    autoApprove: boolean;
    requirePrivacyConsent: boolean;
  };
  pricing: {
    currency: string;
    mode: 'fixed' | 'flags' | 'ticketType';
    flags: PriceFlagDef[];
    matrix: PriceMatrixRow[];
    fallbackPrice?: number;
  };
  payment: {
    mode: 'in_app' | 'instructions_only' | 'none';
    qrProvider: 'none' | 'ips' | 'sepa_epc' | 'custom';
    qrFields?: Record<string, string>;
    deadlineDays?: number;
    deadlineDate?: string;
    ccOnConfirmation?: string;
    reminderDaysBeforeDeadline?: number;
  };
  ticketing: { maxTicketsPerRegistration: number; badgePrintingAtCheckin: boolean };
  branding: {
    eventDisplayName?: string;
    organizerName?: string;
    organizerEmail?: string;
    organizerLogoUrl?: string;
    privacyPolicyUrl?: string;
  };
  sponsors: SponsorsSectionConfig;
}

const EventSettings: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [config, setConfig] = useState<EventConfig | null>(null);
  const [event, setEvent] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [pricingForm] = Form.useForm();
  const [modConfirmNyitva, setModConfirmNyitva] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (!id) return;
    load();
  }, [id]);

  const load = async () => {
    setLoading(true);
    try {
      const [cfg, ev] = await Promise.all([
        api.get(`/events/${id}/config`),
        api.get(`/events/${id}`),
      ]);
      setConfig(cfg.data);
      setEvent(ev.data);
      form.setFieldsValue(cfg.data);
      pricingForm.setFieldsValue({
        currency: cfg.data?.pricing?.currency ?? '',
        mode: biztonsagosMod(cfg.data?.pricing?.mode),
        flags: cfg.data?.pricing?.flags ?? [],
        matrix: cfg.data?.pricing?.matrix ?? [],
        fallbackPrice: cfg.data?.pricing?.fallbackPrice,
      });
    } catch {
      message.error(t('eventSettings.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const saveSection = async (partial: any) => {
    if (!id) return;
    setSaving(true);
    try {
      await api.patch(`/events/${id}/config`, partial);
      message.success(t('eventSettings.saved'));
      await load();
    } catch (e: any) {
      message.error(e.response?.data?.message ?? t('messages.save_error'));
    } finally {
      setSaving(false);
    }
  };

  const toggleSalesClosed = async () => {
    if (!id) return;
    try {
      await api.post(`/events/${id}/sales-closed-toggle`);
      message.success(t('eventSettings.statusUpdated'));
      await load();
    } catch {
      message.error(t('eventSettings.toggleFailed'));
    }
  };

  if (!(config && event)) return <Card loading={loading} />;

  const tabs = [
    {
      key: 'registration',
      label: t('eventSettings.tabRegistration'),
      children: (
        <Form
          form={form}
          layout="vertical"
          initialValues={config}
          onFinish={(v) => saveSection({ registration: v.registration })}
        >
          <Form.Item
            label={t('eventSettings.allowedTypesLabel')}
            name={['registration', 'allowedRegistrantTypes']}
          >
            <Select
              mode="multiple"
              options={[
                { label: t('eventSettings.typeCompany'), value: 'company' },
                { label: t('eventSettings.typeIndividual'), value: 'individual' },
              ]}
            />
          </Form.Item>
          <Form.Item
            label={t('eventSettings.companyFieldsLabel')}
            name={['registration', 'companyFieldsRequired']}
          >
            <Select
              mode="multiple"
              options={[
                { label: t('eventSettings.fieldTaxNumber'), value: 'taxNumber' },
                { label: t('eventSettings.fieldCompanyName'), value: 'name' },
                { label: t('eventSettings.fieldAddress'), value: 'address' },
              ]}
            />
          </Form.Item>
          <Form.Item
            label={t('eventSettings.autoApproveLabel')}
            name={['registration', 'autoApprove']}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label={t('eventSettings.privacyConsentLabel')}
            name={['registration', 'requirePrivacyConsent']}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={saving}>
            {t('eventSettings.saveRegistration')}
          </Button>
        </Form>
      ),
    },
    {
      key: 'pricing',
      label: t('eventSettings.tabPricing'),
      children: (
        <Form
          form={pricingForm}
          layout="vertical"
          onFinish={(v) =>
            saveSection({
              pricing: {
                ...config.pricing,
                currency: (v.currency ?? '').trim(),
                mode: v.mode,
                flags: v.flags,
                matrix: v.matrix,
                fallbackPrice: v.fallbackPrice,
              },
            })
          }
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label={t('eventSettings.currencyLabel')}
                name="currency"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: t('eventSettings.currencyRequired'),
                  },
                ]}
              >
                <Input placeholder={t('eventSettings.currencyPlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label={t('eventSettings.pricingModeLabel')} name="mode">
                <Select
                  options={[
                    { label: t('eventSettings.modeTicketType'), value: 'ticketType' },
                    { label: t('eventSettings.modeFlags'), value: 'flags' },
                    { label: t('eventSettings.modeFixed'), value: 'fixed' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider>{t('eventSettings.flagsDivider')}</Divider>
          <Form.List name="flags">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Space key={field.key} align="baseline" style={{ display: 'flex' }}>
                    <Form.Item name={[field.name, 'key']}>
                      <Input placeholder={t('eventSettings.flagKeyPlaceholder')} />
                    </Form.Item>
                    <Form.Item name={[field.name, 'label']}>
                      <Input
                        placeholder={t('eventSettings.flagLabelPlaceholder')}
                        style={{ width: 280 }}
                      />
                    </Form.Item>
                    <Button danger onClick={() => remove(field.name)}>
                      {t('eventSettings.delete')}
                    </Button>
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add({ key: '', label: '' })}>
                  {t('eventSettings.addFlag')}
                </Button>
              </>
            )}
          </Form.List>

          <Divider>{t('eventSettings.matrixDivider')}</Divider>
          <Text type="secondary">
            {t('eventSettings.matrixHint')} <Tag>{t('eventSettings.matrixExample')}</Tag>
          </Text>
          <Form.List name="matrix">
            {(fields, { add, remove }) => (
              <div style={{ marginTop: 12 }}>
                {fields.map((field) => (
                  <Space key={field.key} align="baseline" style={{ display: 'flex' }}>
                    <Form.Item name={[field.name, 'conditions']}>
                      <ConditionsInput />
                    </Form.Item>
                    <Form.Item name={[field.name, 'price']}>
                      <InputNumber placeholder={t('eventSettings.pricePlaceholder')} min={0} />
                    </Form.Item>
                    <Button danger onClick={() => remove(field.name)}>
                      {t('eventSettings.delete')}
                    </Button>
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add({ conditions: {}, price: 0 })}>
                  {t('eventSettings.addMatrixRow')}
                </Button>
              </div>
            )}
          </Form.List>

          <Form.Item label={t('eventSettings.fallbackPriceLabel')} name="fallbackPrice">
            <InputNumber min={0} />
          </Form.Item>

          {/* Popconfirm, not Modal.confirm: the static method renders outside
              ConfigProvider and loses the panel theme. */}
          <Popconfirm
            open={modConfirmNyitva}
            title={t('eventSettings.pricingModeConfirmTitle')}
            description={t('eventSettings.pricingModeConfirmBody')}
            okText={t('common.save')}
            cancelText={t('common.cancel')}
            onConfirm={() => {
              setModConfirmNyitva(false);
              pricingForm.submit();
            }}
            onCancel={() => setModConfirmNyitva(false)}
            onOpenChange={(nyitva) => {
              if (!nyitva) setModConfirmNyitva(false);
            }}
          >
            <Button
              type="primary"
              loading={saving}
              onClick={async () => {
                try {
                  await pricingForm.validateFields();
                } catch {
                  return;
                }
                if (pricingForm.getFieldValue('mode') !== biztonsagosMod(config.pricing.mode)) {
                  setModConfirmNyitva(true);
                  return;
                }
                pricingForm.submit();
              }}
            >
              {t('eventSettings.savePricing')}
            </Button>
          </Popconfirm>
        </Form>
      ),
    },
    {
      key: 'payment',
      label: t('eventSettings.tabPayment'),
      children: (
        <Form
          layout="vertical"
          initialValues={config.payment}
          onFinish={(v) => saveSection({ payment: v })}
        >
          <Form.Item label={t('eventSettings.modeLabel')} name="mode">
            <Select
              options={[
                { label: t('eventSettings.paymentInstructionsOnly'), value: 'instructions_only' },
                { label: t('eventSettings.paymentInApp'), value: 'in_app' },
                { label: t('eventSettings.paymentNone'), value: 'none' },
              ]}
            />
          </Form.Item>
          <Form.Item label={t('eventSettings.qrProviderLabel')} name="qrProvider">
            <Select
              options={[
                { label: t('eventSettings.qrNone'), value: 'none' },
                { label: t('eventSettings.qrIps'), value: 'ips' },
                { label: t('eventSettings.qrSepa'), value: 'sepa_epc' },
                { label: t('eventSettings.qrCustom'), value: 'custom' },
              ]}
            />
          </Form.Item>

          <Divider>{t('eventSettings.qrFieldsDivider')}</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('eventSettings.qrAccountHolder')}
                name={['qrFields', 'accountHolder']}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('eventSettings.qrAccountNumber')}
                name={['qrFields', 'accountNumber']}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('eventSettings.qrBankName')} name={['qrFields', 'bankName']}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('eventSettings.qrPaymentCode')}
                name={['qrFields', 'paymentCode']}
              >
                <Input placeholder={t('eventSettings.qrPaymentCodePlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('eventSettings.qrPurpose')} name={['qrFields', 'purpose']}>
                <Input placeholder={t('eventSettings.qrPurposePlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('eventSettings.qrModel')} name={['qrFields', 'model']}>
                <Input placeholder="00" />
              </Form.Item>
            </Col>
          </Row>

          <Divider>{t('eventSettings.deadlineDivider')}</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label={t('eventSettings.deadlineDaysLabel')} name="deadlineDays">
                <InputNumber min={0} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label={t('eventSettings.deadlineDateLabel')} name="deadlineDate">
                <Input type="date" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label={t('eventSettings.reminderDaysLabel')}
                name="reminderDaysBeforeDeadline"
              >
                <InputNumber min={1} max={30} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={t('eventSettings.ccLabel')}
            name="ccOnConfirmation"
            extra={t('eventSettings.ccExtra')}
          >
            <Input placeholder={t('eventSettings.ccPlaceholder')} />
          </Form.Item>

          <Button type="primary" htmlType="submit" loading={saving}>
            {t('eventSettings.savePayment')}
          </Button>
        </Form>
      ),
    },
    {
      key: 'ticketing',
      label: t('eventSettings.tabTicketing'),
      children: (
        <Form
          layout="vertical"
          initialValues={config.ticketing}
          onFinish={(v) => saveSection({ ticketing: v })}
        >
          <Form.Item
            label={t('eventSettings.maxTicketsLabel')}
            name="maxTicketsPerRegistration"
            tooltip={t('eventSettings.maxTicketsTooltip')}
            extra={t('eventSettings.maxTicketsExtra')}
          >
            <InputNumber
              min={0}
              style={{ width: 240 }}
              addonAfter={t('eventSettings.ticketUnit')}
            />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={saving}>
            {t('eventSettings.saveTicketing')}
          </Button>
        </Form>
      ),
    },
    {
      // #202 — stored under `ticketing`, but on its own tab: the desk switch
      // has nothing to do with selling tickets. Only the one key is sent; the
      // server merges it into the section, so the other ticketing keys stay.
      key: 'checkin',
      label: t('eventSettings.tabCheckin'),
      children: (
        <Form
          layout="vertical"
          initialValues={{ badgePrintingAtCheckin: config.ticketing.badgePrintingAtCheckin }}
          onFinish={(v) =>
            saveSection({ ticketing: { badgePrintingAtCheckin: v.badgePrintingAtCheckin } })
          }
        >
          <Form.Item
            label={t('eventSettings.badgePrintingLabel')}
            name="badgePrintingAtCheckin"
            valuePropName="checked"
            extra={t('eventSettings.badgePrintingExtra')}
          >
            <Switch />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={saving}>
            {t('eventSettings.saveCheckin')}
          </Button>
        </Form>
      ),
    },
    {
      key: 'branding',
      label: t('eventSettings.tabBranding'),
      children: (
        <Form
          layout="vertical"
          initialValues={config.branding}
          onFinish={(v) => saveSection({ branding: v })}
        >
          <Form.Item label={t('eventSettings.displayNameLabel')} name="eventDisplayName">
            <Input />
          </Form.Item>
          <Form.Item label={t('eventSettings.organizerNameLabel')} name="organizerName">
            <Input />
          </Form.Item>
          <Form.Item label={t('eventSettings.organizerEmailLabel')} name="organizerEmail">
            <Input />
          </Form.Item>
          <Form.Item label={t('eventSettings.privacyUrlLabel')} name="privacyPolicyUrl">
            <Input />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={saving}>
            {t('eventSettings.saveBranding')}
          </Button>
        </Form>
      ),
    },
    {
      key: 'sponsors',
      label: t('eventSettings.tabSponsors'),
      children: (
        <EventSponsorSettings
          eventId={id as string}
          value={config.sponsors}
          currency={config.pricing.currency}
          saving={saving}
          onSave={(sponsors) => saveSection({ sponsors })}
        />
      ),
    },
  ];

  return (
    <Card>
      <Row justify="space-between" align="middle">
        <Col>
          <Title level={3}>{t('eventSettings.title', { event: event.title })}</Title>
          <Text type="secondary">{t('eventSettings.slugLine', { slug: event.slug })}</Text>
        </Col>
        <Col>
          <Space>
            <Text strong>{t('eventSettings.salesClosedLabel')}</Text>
            <Switch checked={event.salesClosed} onChange={toggleSalesClosed} />
          </Space>
        </Col>
      </Row>
      <Divider />
      <Tabs items={tabs} />
    </Card>
  );
};

const ConditionsInput: React.FC<{
  value?: Record<string, boolean>;
  onChange?: (v: Record<string, boolean>) => void;
}> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const json = JSON.stringify(value ?? {}, null, 0);
  return (
    <Input
      placeholder={t('eventSettings.conditionsPlaceholder')}
      value={json}
      style={{ width: 320 }}
      onChange={(e) => {
        try {
          const v = JSON.parse(e.target.value || '{}');
          onChange?.(v);
        } catch {
          /* invalid JSON: ignore */
        }
      }}
    />
  );
};

export default EventSettings;
